import React, { Component } from 'react';
import { isChunkError, recoverFromChunkError } from '../utils/chunkErrorRecovery';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    // External DOM mutation (browser translate extensions, injected scripts) — silently recover
    const isDomMutationError = error.message && (
      error.message.includes('removeChild') ||
      error.message.includes('insertBefore') ||
      error.message.includes('not a child of this node')
    );
    if (isDomMutationError) {
      console.warn('External DOM mutation conflicted with React, recovering...', error.message);
      return { hasError: false, error: null, isChunkError: false };
    }

    // ChunkLoadError — happens after a new deployment when cached chunks are gone
    return { hasError: true, error, isChunkError: isChunkError(error) };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    const isDomMutationError = error.message && (
      error.message.includes('removeChild') ||
      error.message.includes('insertBefore') ||
      error.message.includes('not a child of this node')
    );
    if (isDomMutationError) {
      this.setState({ hasError: false, error: null, isChunkError: false });
      return;
    }

    // Auto-recover for chunk errors — purges SW + CacheStorage and forces
    // a cache-busting reload so the new bundle is fetched from the origin.
    if (this.state.isChunkError) {
      recoverFromChunkError();
      return;
    }

    // Report non-trivial errors to support — sendBeacon avoids console-noise
    // when the backend is briefly returning 5xx.
    try {
      const payload = JSON.stringify({
        status_code: 'REACT_ERROR',
        url: window.location.href,
        message: `${error.name}: ${error.message}\n${errorInfo?.componentStack?.slice(0, 500) || ''}`,
      });
      const reportUrl = `${process.env.REACT_APP_BACKEND_URL}/api/report-error`;
      const blob = new Blob([payload], { type: 'application/json' });
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(reportUrl, blob)) {
        // queued
      } else {
        fetch(reportUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true,
        }).catch(() => {});
      }
    } catch (e) { /* silent */ }
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        return (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minHeight: '100vh', background: '#f8fafc', padding: '24px',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
          }}>
            <div style={{ textAlign: 'center', maxWidth: 420 }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', marginBottom: 24 }}>
                taskeri<span style={{ color: '#f97316' }}>.</span><sup style={{ fontSize: '0.5em' }}>®</sup>
              </div>
              <div style={{
                width: 48, height: 48, background: '#dbeafe', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px', color: '#2563eb', fontSize: 24
              }}>
                &#x21bb;
              </div>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
                New version available
              </h2>
              <p style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.6, marginBottom: 20 }}>
                We've just deployed an update. A quick refresh will get you the latest version.
              </p>
              <button
                onClick={() => recoverFromChunkError({ force: true })}
                style={{
                  background: '#0f172a', color: '#fff', border: 'none',
                  padding: '10px 24px', borderRadius: 8, fontSize: '0.85rem',
                  fontWeight: 600, cursor: 'pointer'
                }}
              >
                Refresh Now
              </button>
            </div>
          </div>
        );
      }

      // Generic error fallback
      return this.props.fallback || (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '100vh', background: '#f8fafc', padding: '24px',
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        }}>
          <div style={{ textAlign: 'center', maxWidth: 420 }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', marginBottom: 24 }}>
              taskeri<span style={{ color: '#f97316' }}>.</span><sup style={{ fontSize: '0.5em' }}>®</sup>
            </div>
            <div style={{
              width: 48, height: 48, background: '#fef3c7', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px', color: '#d97706', fontSize: 24
            }}>
              !
            </div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
              Something went wrong
            </h2>
            <p style={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.6, marginBottom: 20 }}>
              An unexpected error occurred. Please try refreshing the page. If the problem persists, our team has been notified.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => window.location.reload()}
                style={{
                  background: '#0f172a', color: '#fff', border: 'none',
                  padding: '10px 24px', borderRadius: 8, fontSize: '0.85rem',
                  fontWeight: 600, cursor: 'pointer'
                }}
              >
                Refresh Page
              </button>
              <button
                onClick={() => { window.location.href = '/'; }}
                style={{
                  background: '#fff', color: '#475569', border: '1px solid #e2e8f0',
                  padding: '10px 24px', borderRadius: 8, fontSize: '0.85rem',
                  fontWeight: 600, cursor: 'pointer'
                }}
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
